import React from 'react'
import { AuditHistoryItem } from '../../../types/timeOffRequestDetails'
import {
  getCurrentStatusFields,
  ignoredStatuses,
  StatusEntry,
} from '../../../utils/statusFieldsUtils'
import StatusFieldsList from './StatusFieldsList'

interface RequestHistoryProps {
  auditHistory: AuditHistoryItem[] | undefined
  partialRefId: string | undefined | null
  manualReqRcvdDate?: string | null
  isVacationBid?: boolean
  ownerNuid?: string | null
}

const toStatusEntry = (event: AuditHistoryItem, manualReqRcvdDate?: string | null): StatusEntry => ({
  status: event.toffReqstStatus,
  createdTime: event.lastUpdatedTime,
  comments: event.comments,
  denialReason: event.denialReason,
  mgrRequestedRetraction: event.mgrRequestedRetraction,
  firstName: event.firstName,
  lastName: event.lastName,
  fullName: event.fullName,
  approverIdentifier: event.aprvrNuid,
  updatedBy: event.lastUpdatedBy,
  createdBy: event.createdBy,
  torRank: event.torRank,
  manualReqRcvdDate
})

/**
 * Renders each past status change for a time off request from `data.auditHistory`.
 * @param auditHistory - Audit trail of status changes (initial submission is excluded).
 * @param partialRefId - Denied request ID shown for partially-approved history entries.
 * @param ownerNuid - The request owner's nuid, used to tell an actual proxy actor apart from the owner acting on their own request.
 * @returns The history section, or null when there is no approver activity to report yet.
 */
const RequestHistory: React.FC<RequestHistoryProps> = ({
  auditHistory,
  partialRefId,
  manualReqRcvdDate,
  isVacationBid = false,
  ownerNuid,
}) => {
  // Entries with no approver activity to report (e.g. initial submission/draft) never render,
  // regardless of how many of them are mixed into the audit trail
  const visibleHistory = (auditHistory ?? []).filter(
    (event) => !ignoredStatuses.includes(event.toffReqstStatus?.toUpperCase() ?? '')
  )

  if (visibleHistory.length === 0) {
    return null
  }

  return (
    <div className="w-full mt-6 pt-9! pb-[60px]!">
      <h2 className="h2 mb-10!">History</h2>

      <div className="space-y-4 mt-4">
        <div className="space-y-4 mt-4">
          {visibleHistory.map((event, idx) => {
            const statusCode = event.toffReqstStatus?.toUpperCase() ?? ''
            const fields = getCurrentStatusFields(
              toStatusEntry(event, manualReqRcvdDate),
              partialRefId,
              isVacationBid,
              ownerNuid
            )
            const displayFields =
              statusCode === 'PRTSUBMITTED' ||
              statusCode === 'PRTSUBMITTED_ONHOLD' ||
              statusCode === 'PRTSUBMITTED_RELEASED'
                ? fields.filter((field) => field.label !== 'Original Time Off Request ID')
                : fields

            return (
              <div key={`${event.toffReqstStatus}-${idx}`} className="mb-10!">
                <StatusFieldsList fields={displayFields} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default RequestHistory
